import type { Plugin } from "./plugin";

export class PluginRegistry {
  private static readonly plugins = new Map<
    string,
    new (el: HTMLElement) => Plugin
  >();
  private static instances: Plugin[] = [];
  private static readonly instanceMap = new Map<HTMLElement, Plugin>();
  private static mutationObserver: MutationObserver | null = null;
  private static isObserving = false;
  private static enableAnimations = false;
  private static animationDuration = 150;

  static configureAnimations(enabled: boolean, duration = 150) {
    this.enableAnimations = enabled;
    this.animationDuration = duration;
  }

  static register(name: string, plugin: new (el: HTMLElement) => Plugin) {
    this.plugins.set(name, plugin);
  }

  static initAll(root: ParentNode = document) {
    this.destroyAll();

    const allPluginElements =
      root.querySelectorAll<HTMLElement>("[data-plugin]");

    allPluginElements.forEach((el) => {
      const pluginNames = el.dataset.plugin?.split(" ") || [];

      pluginNames.forEach((pluginName) => {
        const trimmedName = pluginName.trim();
        const PluginClass = this.plugins.get(trimmedName);
        if (PluginClass) {
          this.initElement(el, PluginClass, trimmedName);
        }
      });
    });

    if (!this.isObserving) {
      this.startObserving();
    }
  }

  static initElement(
    element: HTMLElement,
    PluginClass?: new (el: HTMLElement) => Plugin,
    pluginName?: string,
  ) {
    const key = pluginName || element.dataset.plugin;
    if (!key) return;

    const existingInstances = this.instances.filter((instance) => {
      const instanceElement = this.getElementByInstance(instance);
      return instanceElement === element;
    });

    const PluginConstructor = PluginClass || this.plugins.get(key);
    if (!PluginConstructor) return;

    const hasThisPluginType = existingInstances.some(
      (instance) => instance.constructor === PluginConstructor,
    );

    if (hasThisPluginType) {
      return;
    }

    try {
      const instance = new PluginConstructor(element);
      instance.init();

      element.classList.add("plugin-ready");
      if (
        this.enableAnimations &&
        !element.classList.contains("plugin-fade-in")
      ) {
        element.classList.add("plugin-fade-in");

        setTimeout(() => {
          element.classList.remove("plugin-fade-in");
        }, this.animationDuration);
      }

      this.instances.push(instance);
      setTimeout(() => {
        const event = new CustomEvent("plugin-initialized", {
          detail: { pluginName: key, element },
        });
        element.dispatchEvent(event);
      }, 0);
      if (!this.instanceMap.has(element)) {
        this.instanceMap.set(element, instance);
      }
    } catch (error) {
      console.error(`Error initializing plugin "${key}":`, error);
      element.classList.add("plugin-ready");
    }
  }

  static startObserving(root: ParentNode = document) {
    if (this.isObserving || !root) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        this.handleAddedNodes(mutation.addedNodes);
        this.handleRemovedNodes(mutation.removedNodes);
      });
    });

    this.mutationObserver.observe(root, {
      childList: true,
      subtree: true,
    });

    this.isObserving = true;
  }

  private static handleAddedNodes(nodes: NodeList) {
    nodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const element = node as HTMLElement;
      this.initializeNewElement(element);
      this.initializeChildPlugins(element);
    });
  }

  private static handleRemovedNodes(nodes: NodeList) {
    nodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const element = node as HTMLElement;
      this.destroyRemovedElement(element);
      this.destroyChildPlugins(element);
    });
  }

  private static initializeNewElement(element: HTMLElement) {
    if (element.dataset?.plugin) {
      const pluginNames = element.dataset.plugin.split(" ");
      pluginNames.forEach((pluginName) => {
        const trimmedName = pluginName.trim();
        const PluginClass = this.plugins.get(trimmedName);
        if (PluginClass) {
          this.initElement(element, PluginClass, trimmedName);
        }
      });
    }
  }

  private static initializeChildPlugins(element: HTMLElement) {
    const allPluginElements =
      element.querySelectorAll<HTMLElement>("[data-plugin]");

    allPluginElements.forEach((el) => {
      const pluginNames = el.dataset.plugin?.split(" ") || [];

      pluginNames.forEach((pluginName) => {
        const trimmedName = pluginName.trim();
        const PluginClass = this.plugins.get(trimmedName);
        if (PluginClass) {
          this.initElement(el, PluginClass, trimmedName);
        }
      });
    });
  }

  private static destroyRemovedElement(element: HTMLElement) {
    if (element.dataset?.plugin) {
      this.destroyElement(element);
    }
  }

  private static destroyChildPlugins(element: HTMLElement) {
    const pluginElements =
      element.querySelectorAll<HTMLElement>("[data-plugin]");
    pluginElements.forEach((el) => {
      this.destroyElement(el);
    });
  }

  static stopObserving() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.isObserving = false;
  }

  static destroyElement(element: HTMLElement) {
    const instancesToDestroy = this.instances.filter((instance) => {
      const instanceElement = this.getElementByInstance(instance);
      return instanceElement === element;
    });

    instancesToDestroy.forEach((instance) => {
      if (typeof instance.destroy === "function") {
        try {
          instance.destroy();
        } catch (error) {
          console.error("Error destroying plugin instance:", error);
        }
      }

      const index = this.instances.indexOf(instance);
      if (index > -1) {
        this.instances.splice(index, 1);
      }
    });

    this.instanceMap.delete(element);
    element.classList.remove("plugin-ready", "plugin-fade-in");
  }

  static initContainer(container: HTMLElement) {
    const allPluginElements =
      container.querySelectorAll<HTMLElement>("[data-plugin]");

    allPluginElements.forEach((el) => {
      const pluginNames = el.dataset.plugin?.split(" ") || [];

      pluginNames.forEach((pluginName) => {
        const trimmedName = pluginName.trim();
        const PluginClass = this.plugins.get(trimmedName);
        if (PluginClass) {
          this.initElement(el, PluginClass, trimmedName);
        }
      });
    });
  }

  static destroyContainer(container: HTMLElement) {
    const pluginElements =
      container.querySelectorAll<HTMLElement>("[data-plugin]");
    pluginElements.forEach((el) => {
      this.destroyElement(el);
    });
  }

  static hasPlugin(element: HTMLElement): boolean {
    return this.instanceMap.has(element);
  }

  static getObserverStatus(): { isObserving: boolean; pluginCount: number } {
    return {
      isObserving: this.isObserving,
      pluginCount: this.instances.length,
    };
  }

  static destroyAll() {
    this.instances.forEach((instance) => {
      if (typeof instance.destroy === "function") {
        instance.destroy();
      }
    });
    this.instances = [];
    this.instanceMap.clear();
    this.stopObserving();
  }

  static getInstance(element: HTMLElement): Plugin | undefined {
    return this.instanceMap.get(element);
  }

  static getInstancesByType<T extends Plugin>(pluginName: string): T[] {
    return this.instances.filter((instance) => {
      const element = this.getElementByInstance(instance);
      return element?.dataset.plugin === pluginName;
    }) as T[];
  }

  static getInstanceBySelector<T extends Plugin>(
    selector: string,
  ): T | undefined {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) return undefined;
    return this.instanceMap.get(element) as T;
  }

  static executeMethod<T extends Plugin>(
    selector: string,
    methodName: keyof T,
    ...args: any[]
  ): boolean {
    const instance = this.getInstanceBySelector<T>(selector);
    if (!instance || typeof instance[methodName] !== "function") {
      return false;
    }

    try {
      (instance[methodName] as Function)(...args);
      return true;
    } catch (error) {
      console.error(
        `Error executing method ${String(methodName)} on plugin:`,
        error,
      );
      return false;
    }
  }

  static executeMethodOnType<T extends Plugin>(
    pluginName: string,
    methodName: keyof T,
    ...args: any[]
  ): number {
    const instances = this.getInstancesByType<T>(pluginName);
    let successCount = 0;

    instances.forEach((instance) => {
      if (typeof instance[methodName] === "function") {
        try {
          (instance[methodName] as Function)(...args);
          successCount++;
        } catch (error) {
          console.error(
            `Error executing method ${String(methodName)} on plugin:`,
            error,
          );
        }
      }
    });

    return successCount;
  }

  private static getElementByInstance(
    instance: Plugin,
  ): HTMLElement | undefined {
    for (const [element, pluginInstance] of this.instanceMap.entries()) {
      if (pluginInstance === instance) {
        return element;
      }
    }
    return undefined;
  }
}
